import React from 'react';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';

export default function NextWorkoutCard({
  nextWorkout, shownWorkout, workoutRungs,
  workoutChoice, setWorkoutChoice,
  scheduleAt, setScheduleAt,
  scheduleAtRef, whenTimerRef,
  onScheduleWorkout, onSaveRecommendedWorkout, onFetchNextWorkout,
}) {
  return (
    <CollapsibleCard title="Next workout" accentColor={T.color.accentWorkout}>
      <p style={{ margin: '0 0 10px' }}>
        <label>
          <span style={{ color: T.color.textLight, fontSize: T.font.sm, marginRight: 6 }}>When</span>
          <input
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => {
              const value = e.target.value;
              setScheduleAt(value);
              scheduleAtRef.current = value;
              if (whenTimerRef.current) clearTimeout(whenTimerRef.current);
              whenTimerRef.current = setTimeout(() => onFetchNextWorkout(value), 400);
            }}
          />
        </label>
        <span style={{ color: T.color.textLight, fontSize: T.font.sm, marginLeft: 8 }}>
          Options reflect your recovery at this time, not right now.
        </span>
      </p>
      {shownWorkout ? (
        <>
          <p style={{ color: T.color.textMuted, marginTop: 0 }}>
            For {nextWorkout?.when || 'this start'}
            {nextWorkout?.leftover_hours != null
              ? ` · ~${nextWorkout.leftover_hours} h recovery remaining`
              : ''}
            {shownWorkout.duration_min
              ? ` · ${shownWorkout.duration_display}`
              : ''}
            {shownWorkout.distance_mi
              ? ` · ~${shownWorkout.distance_mi} mi`
              : ''}
          </p>
          {workoutRungs.length > 1 && (
            <p style={{ margin: '0 0 10px' }}>
              {workoutRungs.map((w) => {
                const key = w.rung || w.id;
                const selected = shownWorkout && (shownWorkout.rung || shownWorkout.id) === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setWorkoutChoice(key)}
                    style={{
                      marginRight: 8,
                      marginBottom: 6,
                      fontWeight: selected ? 700 : 400,
                      background: selected ? T.color.primary : undefined,
                      color: selected ? '#fff' : undefined,
                      borderColor: selected ? T.color.primary : undefined,
                      borderRadius: T.radius.sm,
                    }}
                  >
                    {w.rung_label || w.name}
                  </button>
                );
              })}
            </p>
          )}
          {nextWorkout?.rungs?.length > 0 && (
            <p style={{ color: T.color.textLight, fontSize: T.font.sm, margin: '0 0 8px' }}>
              Recommended is our suggested workout. Stimulus is a harder option. Not medical advice.
            </p>
          )}
          <p style={{ margin: '0 0 8px' }}>
            <strong>{shownWorkout.name}</strong>
            {shownWorkout.recipe ? ` · ${shownWorkout.recipe}` : ''}
          </p>
          {shownWorkout.rung_note && (
            <p style={{ color: T.color.textMuted, fontSize: T.font.sm, margin: '0 0 8px' }}>{shownWorkout.rung_note}</p>
          )}
          {shownWorkout.steps && shownWorkout.steps.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px 4px 0' }}>Step</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Pace</th>
                  <th style={{ textAlign: 'left', padding: '4px 0' }}>HR</th>
                </tr>
              </thead>
              <tbody>
                {shownWorkout.steps.map((step, i) => (
                  <tr key={`${step.band}-${i}`}>
                    <td style={{ padding: '4px 8px 4px 0' }}>
                      {step.label || step.band}
                    </td>
                    <td style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums' }}>
                      {step.duration_display}
                    </td>
                    <td style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums' }}>
                      {step.pace_low && step.pace_high
                        ? `${step.pace_low}–${step.pace_high}`
                        : step.pace_target || '—'}
                    </td>
                    <td style={{ padding: '4px 0', fontVariantNumeric: 'tabular-nums' }}>
                      {step.hr_low != null && step.hr_high != null
                        ? `${step.hr_low}–${step.hr_high}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={{ color: T.color.textMuted }}>{shownWorkout.why}</p>
          {shownWorkout.id !== 'rest' && shownWorkout.duration_min >= 15 && (
            <p>
              <button type="button" onClick={() => onScheduleWorkout(shownWorkout)}>
                Schedule this
              </button>
              {' '}
              <button type="button" onClick={() => onSaveRecommendedWorkout(shownWorkout)}>
                Save to my workouts
              </button>
              <span style={{ color: T.color.textLight, fontSize: T.font.sm, marginLeft: 8 }}>
                Uses the time above. After scheduling, invite a partner from Scheduled runs.
              </span>
            </p>
          )}
          {nextWorkout?.mix?.n > 0 && (
            <p style={{ color: T.color.textLight, fontSize: T.font.sm, marginTop: 8 }}>
              Last {nextWorkout.mix.window_days} days ({nextWorkout.mix.n} runs):{' '}
              {nextWorkout.mix.counts.easy} easy · {nextWorkout.mix.counts.tempo} tempo ·{' '}
              {nextWorkout.mix.counts.speed} speed · {nextWorkout.mix.counts.long} long
              {nextWorkout.mix.short_pattern ? ' · runs have been short' : ''}
            </p>
          )}
          {nextWorkout?.mix?.week_target_min && (
            <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>
              {nextWorkout.mix.week_minutes
                ? `Last 7 days ${(nextWorkout.mix.week_minutes / 60).toFixed(1)} h`
                : 'Last 7 days —'}
              {nextWorkout.mix.week_miles
                ? ` · ~${nextWorkout.mix.week_miles} mi`
                : ''}
              {nextWorkout.mix.week_walk_minutes
                ? ` · walks ${nextWorkout.mix.week_walk_minutes} min`
                : ''}
              {` · ${nextWorkout.mix.goal_label || 'goal'} ${(nextWorkout.mix.week_target_min[0] / 60).toFixed(1)}–${(nextWorkout.mix.week_target_min[1] / 60).toFixed(1)} h/week`}
              {nextWorkout.mix.long_target_mi
                ? ` · long ${nextWorkout.mix.long_target_mi[0]}–${nextWorkout.mix.long_target_mi[1]} mi`
                : nextWorkout.mix.long_target_min
                ? ` · long ${nextWorkout.mix.long_target_min[0]}–${nextWorkout.mix.long_target_min[1]} min`
                : ''}
              {nextWorkout.mix.volume_status === 'low' ? ' · volume is light' : ''}
              {nextWorkout.mix.volume_status === 'high' ? ' · volume is already ample' : ''}
            </p>
          )}
        </>
      ) : (
        <p>Sync runs and recovery to get a stepped session with your paces.</p>
      )}
    </CollapsibleCard>
  );
}
